import { cn } from "@/common/helpers/utils";
import { useFinePointer } from "@/common/hooks/use-fine-pointer";
import { usePaletteCommands } from "@/components/command-palette/use-palette-commands";
import type { PaletteCommand } from "@/components/command-palette/types";
import { useActiveTab } from "@/components/detail-page/_hooks/use-active-tab";
import { usePermittedItems } from "@/components/detail-page/_hooks/use-permitted-items";
import { renderTabContent, type DetailTab } from "@/components/detail-page/types";
import { NoPermissionCard } from "@/components/no-permission-card/no-permission-card";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import postHog from "@/config/posthog";
import { useCallback, useEffect, useRef, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { CollapsingTabBar } from "./_components/tabbar/collapsing-tab-bar";
import { ScrollableTabBar } from "./_components/tabbar/scrollable-tab-bar";

export const DEFAULT_TAB_STATE_KEY = "tab";
export const DETAIL_TAB_SELECTED_EVENT = "DETAIL_TAB_SELECTED";

interface DetailTabsProps {
  sections: DetailTab[];
  stateKey?: string;
  /** Enables PostHog tab usage tracking under this detail view name. */
  trackingName?: string;
  className?: string;
}

export function DetailTabs({
  sections,
  stateKey = DEFAULT_TAB_STATE_KEY,
  trackingName,
  className,
}: DetailTabsProps): ReactElement {
  const { t } = useTranslation();
  const permittedSections = usePermittedItems(sections);
  const hasFinePointer = useFinePointer();
  const { activeTab, selectTab } = useActiveTab(permittedSections, stateKey);

  const selectedTabRef = useRef(activeTab);

  useEffect(() => {
    selectedTabRef.current = activeTab;
  }, [activeTab]);

  const handleSelectTab = useCallback(
    (value: string) => {
      const previousTab = selectedTabRef.current;
      selectedTabRef.current = value;

      if (trackingName && value !== previousTab) {
        postHog.capture(DETAIL_TAB_SELECTED_EVENT, { detailView: trackingName, tab: value });
      }
      selectTab(value);
    },
    [selectTab, trackingName],
  );

  usePaletteCommands(
    permittedSections
      .filter((section) => section.id !== activeTab)
      .map((section): PaletteCommand => ({
        id: `tab-${section.id}`,
        title: t("CommandPaletteShowTab", { tab: section.label }),
        icon: section.icon && <section.icon />,
        keywords: ["tab"],
        perform: () => handleSelectTab(section.id),
      })),
  );

  if (permittedSections.length === 0) {
    return <NoPermissionCard />;
  }

  return (
    <Tabs value={activeTab} onValueChange={handleSelectTab} className={cn("relative gap-6", className)}>
      {hasFinePointer ? (
        <CollapsingTabBar sections={permittedSections} activeTab={activeTab} onSelectTab={handleSelectTab} />
      ) : (
        <ScrollableTabBar sections={permittedSections} activeTab={activeTab} />
      )}
      {permittedSections.map((section) => (
        <TabsContent key={section.id} value={section.id}>
          {section.id === activeTab && renderTabContent(section)}
        </TabsContent>
      ))}
    </Tabs>
  );
}
